import axios, { AxiosError, AxiosInstance } from 'axios';
import FirebaseAuthManager from './firebaseAuthManager';
import { getDefaultEnv, setupThrottle } from './index';

export interface AuthData {
  firebaseUid: string;
  kvToken: string;
  refreshToken: string;
  uid: string;
  jid: string;
  email: string;
  expireAt: number;
};


export interface ApiTokenResponse {
  status: true;
  status_text: string;
  kv_token: string;
  refreshToken: string;
  uid: string;
  jid: string;
  email: string;
}

export interface ApiRefreshTokenResponse {
  refreshToken: string;
  kv_token: string;
  iat: number;
  exp: number;
}

export interface IsPremium {
  data: {
    is_premium: boolean;
  };
}

export interface SubScription {
  data: {
    subscription_id: string;
    has_active_subscription: boolean;
  };
}

export enum ErrorType {
  ForceSignOut = 'forceSignOut',
  NetworkError = 'networkError',
}

export enum Env {
  Dev = 'dev',
  PreProd = 'pre-prod',
  Prod = 'prod',
}

const testDomain = 'https://api.my-alfred-dev.com';
const preProdDomain = 'https://api.my-alfred-pre.com';
const prodDomain = 'https://api.my-alfred.com';

const paypalTestDomain = 'https://api-paypal.my-alfred-dev.com';
const paypalPreProdDomain = 'https://api-paypal.my-alfred-pre.com';
const paypalProdDomain = 'https://api-paypal.my-alfred.com';

// prod: 3days, dev: 10mins
const testTokenTimeoutTime = 10 * 60 * 1000;
const prodTokenTimeoutTime = 3 * 24 * 60 * 60 * 1000;

const sandboxPayPalClientId =
  'Adu7V-WiUhoHAtYJSZrPQpeWLKQ3-h7Wo7EQtGDyXAhfyYaW9sckgFYsNGTVz3OW4fBJ3GUbZhQ2VrZW';
const livePayPalClientId =
  'Af1Kfwy11dHmE0-j_XD2ua3WHKsGM1-ZNKDacVbYfcAfK_6jzmgR60z6is_QV_iWZW4uadY44c7F2XOm';

class ApiManager {
  static apiInstance: AxiosInstance;
  static env: Env | null = getDefaultEnv();

  static get domain() {
    switch (ApiManager.env) {
      case Env.Prod:
        return prodDomain;
      case Env.PreProd:
        return preProdDomain;
      default:
        return testDomain;
    }
  }

  static get paypalDomain() {
    switch (ApiManager.env) {
      case Env.Prod:
        return paypalProdDomain;
      case Env.PreProd:
        return paypalPreProdDomain;
      default:
        return paypalTestDomain;
    }
  }

  static get tokenTimeoutTime() {
    switch (ApiManager.env) {
      case Env.Prod:
      case Env.PreProd:
        return prodTokenTimeoutTime;
      default:
        return testTokenTimeoutTime;
    }
  }

  static get paypalClientId() {
    switch (ApiManager.env) {
      case Env.Prod:
      case Env.PreProd:
        return livePayPalClientId;
      default:
        return sandboxPayPalClientId;
    }
  }

  static async getIdToken(forceRefresh: boolean) {
    const firebaseAuthManager = new FirebaseAuthManager();
    const user = firebaseAuthManager.getCurrentUser();
    if (user && (user.isAnonymous || user.emailVerified) && user.refreshToken) {
      return user.getIdToken(forceRefresh);
    }
    return null;
  }

  static createAxiosInstance({
    kvToken,
    uid,
  }: {
    kvToken: string;
    uid: string;
  }) {
    const instance = axios.create({
      baseURL: ApiManager.domain,
      headers: {
        Authorization: `Bearer ${kvToken}`,
      },
      params: {
        uid,
      },
    });
    ApiManager.apiInstance = instance;
  }

  static async getKVToken(token: string, resource: string, firebaseUid: string, productVersion?: string) {
    try {
      const params: Record<string, unknown> = {
        fir_uid: firebaseUid,
      }

      if (productVersion) {
        params['version'] = productVersion
      }

      const res = await axios.post<ApiTokenResponse>(
        `${ApiManager.domain}/v2.5/user/authbytoken`,
        {
          type: 'fir_token',
          token,
          resource,
        },
        {
          params
        }
      );
      ApiManager.createAxiosInstance({
        kvToken: res.data.kv_token,
        uid: res.data.uid,
      });
      return {
        data: res.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          error,
          errorStatus: error.response?.status,
        };
      }
    }
  }

  static async getKvTokenAndResetData({
    firebaseUid,
    idToken,
    resource,
    localStorageKey,
    startTime,
  }: {
    firebaseUid: string;
    idToken: string;
    resource: string;
    localStorageKey: string;
    startTime: number;
  }) {
    const res = await ApiManager.getKVToken(idToken, resource, firebaseUid);
    if (res?.data) {
      ApiManager.createAxiosInstance({
        kvToken: res.data.kv_token,
        uid: res.data.uid,
      });
      const authData: AuthData = {
        firebaseUid,
        kvToken: res.data.kv_token,
        refreshToken: res.data.refreshToken,
        email: res.data.email,
        jid: res.data.jid,
        uid: res.data.uid,
        expireAt: startTime + ApiManager.tokenTimeoutTime,
      }
      localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          auth: authData,
        })
      );
      return authData;
    } else {
      throw new Error(res?.error.message);
    }
  }

  static updateToken = setupThrottle(ApiManager.exchangeKVtokenByRefreshToken);

  static async exchangeKVtokenByRefreshToken({
    refreshToken,
    jid,
    firebaseUid
  }: {
    refreshToken: string;
    jid: string;
    firebaseUid: string
  }): Promise<Pick<ApiRefreshTokenResponse, 'refreshToken' | 'kv_token'>> {
    try {
      const res = await axios.post<ApiRefreshTokenResponse>(
        `${ApiManager.domain}/v2.5/user/authbyRefreshToken`,
        {
          refreshToken,
          jid,
        }
      );
      return res.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorResponse = error.response;
        if (
          errorResponse?.status === 401 &&
          errorResponse.data.error_code === 40102
        ) {
          const idToken = await ApiManager.getIdToken(true);
          if (!idToken) {
            throw new Error(ErrorType.ForceSignOut);
          }
          const resource = jid.split('/')[1];
          const res = await ApiManager.getKVToken(idToken, resource, firebaseUid);
          if (res?.data) {
            return res.data;
          } else if (res?.errorStatus === 403 || res?.errorStatus === 500) {
            const refreshedIdToken = await ApiManager.getIdToken(true);
            if (refreshedIdToken) {
              const secondResponse = await ApiManager.getKVToken(
                refreshedIdToken,
                resource,
                firebaseUid
              );
              if (secondResponse?.data) {
                return secondResponse.data;
              } else if (
                secondResponse?.errorStatus === 403 ||
                secondResponse?.errorStatus === 500
              ) {
                throw new Error(ErrorType.ForceSignOut);
              } else {
                throw new Error(ErrorType.NetworkError);
              }
            }
          } else {
            throw new Error(ErrorType.NetworkError);
          }
        } else if (
          errorResponse?.status === 403 &&
          errorResponse?.data.error_code === 40399
        ) {
          throw new Error(ErrorType.ForceSignOut);
        } else {
          throw new Error(ErrorType.NetworkError);
        }
      }
      throw error;
    }
  }

  static async getPaypalSubscriptionId(planKey: string, offeringId: string) {
    const res = await ApiManager.apiInstance.post<SubScription>(
      '/paypal/v1/receipts/subscription',
      {
        plan_key: planKey,
        offering_id: offeringId,
        campaign: 'official_web',
      },
      {
        baseURL: this.paypalDomain,
      }
    );

    return {
      subscriptionId: res.data.data.subscription_id,
      hasActiveSubscription: res.data.data.has_active_subscription,
    };
  }

  static async payPalSubscriptionComplete(subscriptionId: string) {
    const res = await ApiManager.apiInstance.post(
      `/paypal/v1/receipts/subscription/${subscriptionId}`,
      {},
      {
        baseURL: this.paypalDomain,
      }
    );

    return res.data;
  }
}

export default ApiManager;
